
import { TYPE, cannotGo, canOnlyGo } from "./network/network_api";
import Risk from "./risk";
import UI from './ui';
import Roulette from "./roulette_driver";
import Jackpot from "./jackpot_driver";
import { REQUEST_TYPE } from "./network/net";
import Slot from "./slot";
import Balance from "./balance";
import Autoplay from "./autoplay";
import Help from "./help";
import Stream from "./frp/stream";
import Bonus from "./bonus";
import ProgressiveJackpot from "progressive_jackpot/progressive_jackpot"

/** Glues all the game drivers together so making it the uppermost driver.
 * @param leaves Object of views/interactives to pass to specific drivers. Moved away from drivers into some upper code to make implementation of automatic tests possible.
 */
export default function(
    space,
    leaves,
    mathConfig,
    bonusSettings,
    spec
)
{
    const $state = space.s( "state" );
    space.s( "net" )
        .map( net => net.state )
        .to( $state );
    
    const $request = space.s( "request" );
    const $pending = space.s( "pending", undefined, true );
    const $error = space.s( "error" );
    
    $error
        .map( () => true )
        .to( space.s( "isError", false ) );
    
    $state.on( s => console.log( JSON.stringify( s.state ) ) );
    $state.on( s =>
        s.state.debug_info && s.state.debug_info.game_scenario && s.state.debug_info.game_stage_id
        ?
        console.log( window.location.href.split( "?" )[ 0 ] + "?scenario=" + s.state.debug_info.game_scenario.original_index + "&stage=" + s.state.debug_info.game_stage_id.idx + "&lines=" + s.state.debug_info.game_scenario.line_id + "&bet=" + s.state.multiplier )
        :
        undefined
    );
    
    const balance = Balance( space, $state );
    balance.s( "winFlow" ).to( leaves.ui.space.s( "win" ) );
    
    //RISK:
    const risk = Risk(
        space,
        leaves.risk,
        spec.risks
    );
    
    //UI:
    const ui = UI(
        space,
        balance.s( "credits" ),
        mathConfig,
        leaves.ui.space,
    );
    Help( leaves.ui.space, leaves.help );
    leaves.ui.space.s( "collect" )
        .map( () => ({ type : REQUEST_TYPE.PROCEED }) )
        .to( $request );
    
    const slot = Slot(
        space,
        ui,
        leaves,
    );
    leaves.slot.space.glue( "winFlowUp", balance );
    leaves.freegames.space.glue( "winFlowUp", balance );
    const $winFlowCompleteSlot = leaves.slot.space.s( "winFlowComplete" );
    const $winFlowCompleteFreegames = leaves.freegames.space.s( "winFlowComplete" );
    balance.s( "winFlowComplete" )
        .with( $state, ( complete, state ) => ({ complete, state }) )
        .on( ({ complete, state }) =>
        {
            $winFlowCompleteSlot.next( complete );
            $winFlowCompleteFreegames.next( complete );
        } );
    money_up_sound( space, leaves, balance )
    slot.space.s( "displayingWin" ).to( leaves.ui.space.s( "displayingWin" ) );
    
    slot.barrel.s( "initiate" )
        .withLatestFrom( $state, ( i, state ) => state.type )
        .filter( type => type == TYPE.FINAL )
        .withLatestFrom( leaves.ui.space.s( "price" ), ( i, price ) => price )
        .map( price => - price )
        .subscribe( balance.s( "creditsChange" ) );
    
    leaves.ui.space.s( "gamble" )
        .map( () => ({ type : REQUEST_TYPE.ENTER_RISK }) )
        .subscribe( $request );
    
    const roulette = Roulette(
        $state,
        $request,
        leaves.roulette,
        leaves.ui.space.s( "startKey" ),
        space,
    );
    roulette.$startImpulse.subscribe( leaves.ui.space.s( "startImpulse" ) );
    
    const jackpot = Jackpot(
        $state,
        $request,
        leaves.jackpot,
        leaves.ui.space.s( "start" ),
        space.s( "lastRequest" ),
        space,
    );
    jackpot.s( "isIn" ).to( leaves.ui.space.s( "isJackpot" ) );
    
    const autoplay = Autoplay(
        $request,
        $error,
        leaves.ui.space,
        slot.space.s( "winningRegularStop" ),
        space,
    );
    autoplay.glue( "initiate", slot.barrel );
    
    const bonus = Bonus(
        space,
        leaves,
        bonusSettings
    );
    
    //start feature invitation:
    $state
        .map( () => false )
        .merge( $pending.map( () => false ) )
        .merge( slot.space.s( "barrelStop" ).map( state =>
            state.type == TYPE.REGULAR
            &&
            cannotGo( TYPE.FINAL, state.state ) )
        )
        .distinctUntilChanged()
        .subscribe( leaves.ui.space.s( "canStartFeature" ) );
    
    //risk invitation:
    $state
        .map( state => state.type == TYPE.FINISH )
        .merge( slot.space.s( "barrelStop" ).withLatestFrom(
            $state.map( state =>
                //in some games barrelStop event thrown AFTER game switches to FINISH state:
                state.type == TYPE.FINISH
                ||
                (
                    //no matter after regular spin or bonus:
                    state.state.stage.win > 0 &&
                    //risk doesn't count as state you can go (it's a separate scenario), so if no bonuses available (after regular spin without bonuses won or after bonuses):
                    canOnlyGo( TYPE.FINAL, state.state )
                )
            ),
            ( stop, mappedState ) => mappedState
        ) )
        //any server interaction cancels invitation:
        .merge( $pending
            .filter( pending => pending != undefined )
            .map( () => false )
        )
        .distinctUntilChanged()
        .subscribe( leaves.ui.space.s( "riskInvitation" ) );
    
    //false when the whole user interface must be disabled (become non-interactive with respectful visible representation), true otherwise:
    Stream.combineLatest(
        $pending,
        slot.space.s( "rotating" ),
        risk.s( "choosingCard" ),
        jackpot.s( "isIn" ),
        space.s( "isError" ),
        //true when there is some animation going on which cannot be cancelled or intervened in any way:
        space.s( "animating" ),
    )
    .map( merge => _.every( merge, value => ! value ) )
    .subscribe( leaves.ui.space.s( "enabled" ) );
    
    //COLLECT button:
    //enabled:
    leaves.ui.space.s( "enabled" )
        .combineLatest(
            jackpot.s( "isIn" ),
            space.s( "animating" ),
            space.s( "state" ),
            ( enabled, isJackpot, animating, state ) => enabled || ( isJackpot && ! animating ) || state.type == TYPE.PROGRESSIVE_JACKPOT
        )
        .subscribe( leaves.ui.space.s( "collectEnabled" ) );
    //visible:
    leaves.ui.space.s( "riskInvitation" )
        .combineLatest(
            space.s( "isRisk" ),
            leaves.ui.space.s( "mayStop" ),
            leaves.ui.space.s( "spinVisible" ),
            space.s( "state" ),
            $pending,
            ( invitation, isRisk, mayStop, spinVisible, state, pending ) =>
                (
                    invitation
                    ||
                    isRisk
                )
                &&
                ! mayStop
                &&
                ! spinVisible
                ||
                pending == REQUEST_TYPE.ENTER_RISK
                ||
                state.type == TYPE.PROGRESSIVE_JACKPOT
        )
        .subscribe( leaves.ui.space.s( "collectVisible" ) );
    
    //SPIN button is pretty complicated because it hides network "COLLECT" operation:
    $pending
        .any(
            $state,
            leaves.ui.space.s( "canBet" ),
            ( pending, state, canBet ) =>
                canBet
                ||
                (
                    canOnlyGo( TYPE.FINAL, state )
                    &&
                    _.includes( [ REQUEST_TYPE.PROCEED, REQUEST_TYPE.TAKE_WIN ], pending )
                )
        )
        .to( leaves.ui.space.s( "spinVisible" ) );
    
    risk.s( "animating" )
        .combineLatest(
            slot.space.s( "animating" ),
            roulette.$animating,
            jackpot.s( "flowing" ),
        )
        .map( merge => _.some( merge ) )
        .subscribe( space.s( "animating", false ) );
    
    $state
        .any( space.s( "isRisk" ), ( state, isRisk ) => ({ state, isRisk }) )
        .filter( ({ state, isRisk }) => state.type == TYPE.FINAL && ! isRisk )
        .map( ({ state }) => state )
        .to( slot.space.s( "readyToSpin" ) );
    
    ProgressiveJackpot( space, leaves.progressive_jackpot )
};

function money_up_sound( space, leaves, balance )
{
    //last 2 unique server states:
    const state_pair = []
    balance.s( "money" )
        .distinctUntilChanged()
        .pairwise()
        //ignore JACKPOT collect:
        .with(
            space.s( "state" ).map( state => {
                if ( state_pair[ state_pair.length - 1 ] != state.type )
                {
                    state_pair.push( state.type )
                }
                while ( state_pair.length > 2 ) state_pair.shift()
            } ),
            ( pair, _ ) => pair )
        .filter( () =>
            state_pair[ 1 ] != TYPE.JACKPOT
            &&
            state_pair[ 1 ] != TYPE.REGULAR
            //uncomment if you DO NOT want progressive jackpot enter to play coins sound:
            /*&&
            state_pair[ 1 ] != TYPE.PROGRESSIVE_JACKPOT*/
            //from progressive jackpot to progressive jackpot:
            &&
            (
                state_pair[ 0 ] != TYPE.PROGRESSIVE_JACKPOT
                ||
                state_pair[ 1 ] != TYPE.PROGRESSIVE_JACKPOT
            )
            //from progressive jackpot to FINAL:
            &&
            (
                state_pair[ 0 ] != TYPE.PROGRESSIVE_JACKPOT
                ||
                state_pair[ 1 ] != TYPE.FINAL
            )
        )
        .on( pair => {
            if ( pair[ 1 ] > pair[ 0 ] && leaves.slot.slotScene.sndCredits )
            {
                leaves.slot.slotScene.sndCredits.play()
            }
        } )
}





// WEBPACK FOOTER //
// ./src/library/game.es6